import {
  Content,
  Group,
  Icon,
  Item,
  ItemIndicator,
  ItemText,
  Label,
  Portal,
  Root,
  ScrollDownButton,
  ScrollUpButton,
  Separator,
  Trigger,
  Value,
  Viewport,
} from "@radix-ui/react-select";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { ComponentProps, FC } from "react";

import { cn } from "@/lib/utils";

type SelectProps = ComponentProps<typeof Root>;
type SelectGroupProps = ComponentProps<typeof Group>;
type SelectValueProps = ComponentProps<typeof Value>;
type SelectTriggerProps = ComponentProps<typeof Trigger>;
type SelectContentProps = ComponentProps<typeof Content>;
type SelectLabelProps = ComponentProps<typeof Label>;
type SelectItemProps = ComponentProps<typeof Item>;
type SelectSeparatorProps = ComponentProps<typeof Separator>;
type SelectScrollUpButtonProps = ComponentProps<typeof ScrollUpButton>;
type SelectScrollDownButtonProps = ComponentProps<typeof ScrollDownButton>;

export const Select: FC<SelectProps> = (props) => (
  <Root data-slot="select" {...props} />
);

export const SelectGroup: FC<SelectGroupProps> = (props) => (
  <Group data-slot="select-group" {...props} />
);

export const SelectValue: FC<SelectValueProps> = (props) => (
  <Value data-slot="select-value" {...props} />
);

export const SelectTrigger: FC<SelectTriggerProps> = ({
  className,
  children,
  ...props
}) => (
  <Trigger
    data-slot="select-trigger"
    className={cn(
      "border-input data-[placeholder]:text-muted-foreground aria-invalid:border-destructive ring-ring/10 dark:ring-ring/20 dark:outline-ring/40 outline-ring/50 [&_svg:not([class*='text-'])]:text-muted-foreground flex h-9 w-full items-center justify-between rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] focus-visible:ring-4 focus-visible:outline-1 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:focus-visible:ring-0 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&>span]:line-clamp-1",
      className,
    )}
    {...props}
  >
    {children}
    <Icon asChild>
      <ChevronDownIcon className="size-4 opacity-50" />
    </Icon>
  </Trigger>
);

export const SelectContent: FC<SelectContentProps> = ({
  className,
  children,
  position = "popper",
  ...props
}) => (
  <Portal>
    <Content
      data-slot="select-content"
      className={cn(
        "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border shadow-md",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className,
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <Viewport
        className={cn(
          "p-1",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1",
        )}
      >
        {children}
      </Viewport>
      <SelectScrollDownButton />
    </Content>
  </Portal>
);

export const SelectLabel: FC<SelectLabelProps> = ({ className, ...props }) => (
  <Label
    data-slot="select-label"
    className={cn("px-2 py-1.5 text-sm font-medium", className)}
    {...props}
  />
);

export const SelectItem: FC<SelectItemProps> = ({
  className,
  children,
  ...props
}) => (
  <Item
    data-slot="select-item"
    className={cn(
      "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
      className,
    )}
    {...props}
  >
    <span className="absolute right-2 flex size-3.5 items-center justify-center">
      <ItemIndicator>
        <CheckIcon className="size-4" />
      </ItemIndicator>
    </span>
    <ItemText>{children}</ItemText>
  </Item>
);

export const SelectSeparator: FC<SelectSeparatorProps> = ({
  className,
  ...props
}) => (
  <Separator
    data-slot="select-separator"
    className={cn("bg-border pointer-events-none -mx-1 my-1 h-px", className)}
    {...props}
  />
);

export const SelectScrollUpButton: FC<SelectScrollUpButtonProps> = ({
  className,
  ...props
}) => (
  <ScrollUpButton
    data-slot="select-scroll-up-button"
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronUpIcon className="size-4" />
  </ScrollUpButton>
);

export const SelectScrollDownButton: FC<SelectScrollDownButtonProps> = ({
  className,
  ...props
}) => (
  <ScrollDownButton
    data-slot="select-scroll-down-button"
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronDownIcon className="size-4" />
  </ScrollDownButton>
);
